import { useQuery } from '@apollo/client';
import { useCallback, useMemo } from 'react';
import { SELECTION_SEPARATED_FIELDS } from '../apollo/cache/selection-separated-fields';

const withOperationConnectionKey = (document) => {
  const operation = document.definitions.find(
    (def) => def.kind === 'OperationDefinition',
  );
  if (!operation?.name) return document;
  const operationName = operation.name.value;
  let didInject = false;
  const selections = operation.selectionSet.selections.map((selection) => {
    if (
      selection.kind !== 'Field'
      || !SELECTION_SEPARATED_FIELDS.has(selection.name.value)
      || (selection.directives || []).some((dir) => dir.name.value === 'connection')
    ) {
      return selection;
    }
    didInject = true;
    return {
      ...selection,
      directives: [
        ...(selection.directives || []),
        {
          kind: 'Directive',
          name: { kind: 'Name', value: 'connection' },
          arguments: [
            {
              kind: 'Argument',
              name: { kind: 'Name', value: 'key' },
              value: { kind: 'StringValue', value: operationName, block: false },
            },
          ],
        },
      ],
    };
  });
  if (!didInject) return document;
  return {
    ...document,
    definitions: document.definitions.map((def) => (def === operation
      ? { ...operation, selectionSet: { ...operation.selectionSet, selections } }
      : def)),
  };
};

export const useFilteredQuery = (queryDocument, options) => {
  const transformedDocument = useMemo(
    () => withOperationConnectionKey(queryDocument),
    [queryDocument],
  );
  const {
    data, previousData, client, refetch, variables, ...result
  } = useQuery(transformedDocument, options);
  const getQueryName = useCallback((document) => {
    const operationDefinitions = document.definitions.filter((def) => def.kind === 'OperationDefinition');
    return operationDefinitions[0].selectionSet.selections[0].name.value;
  }, []);
  const deleteQueryFromCache = useCallback(() => {
    const rootQuery = client.cache.data.data.ROOT_QUERY;
    const queryName = getQueryName(queryDocument);
    const matcher = new RegExp(`^(${queryName}):{.*}$`);
    Object.keys(rootQuery).forEach((key) => {
      if (key.match(matcher)) {
        client.cache.evict({ id: 'ROOT_QUERY', fieldName: key });
      }
    });
  }, [client, queryDocument]);

  const deleteOwnQueryFromCache = useCallback(() => {
    const operation = transformedDocument.definitions.find((def) => def.kind === 'OperationDefinition');
    const field = operation.selectionSet.selections[0];
    const storeFieldName = client.cache.policies.getStoreFieldName({
      typename: 'Query',
      fieldName: field.name.value,
      field,
      variables,
    });
    client.cache.evict({ id: 'ROOT_QUERY', fieldName: storeFieldName });
  }, [client, transformedDocument, variables]);

  const deletePaginatedAndRefetch = useCallback(() => {
    deleteQueryFromCache();
    refetch(variables);
  }, [client, queryDocument, variables, deleteQueryFromCache, getQueryName, refetch]);

  const deleteOwnPaginatedAndRefetch = useCallback(() => {
    deleteOwnQueryFromCache();
    refetch(variables);
  }, [variables, deleteOwnQueryFromCache, refetch]);
  return {
    data,
    previousData,
    usableData: data || previousData,
    refetch,
    client,
    variables,
    deletePaginatedAndRefetch,
    deleteOwnPaginatedAndRefetch,
    deleteQueryFromCache,
    deleteOwnQueryFromCache,
    ...result,
  };
};

export default useFilteredQuery;
